import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import UnifiedSearch from '../pages/UnifiedSearch';

const NavBar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isResourcesDropdownOpen, setIsResourcesDropdownOpen] = useState(false);
  const [isChartsDropdownOpen, setIsChartsDropdownOpen] = useState(false);
  const [isMobileChartsOpen, setIsMobileChartsOpen] = useState(false); // New state for mobile charts
  const [isMobileResourcesOpen, setIsMobileResourcesOpen] = useState(false); // New state for mobile resources
  const [isCatalogDropdownOpen, setIsCatalogDropdownOpen] = useState(false);
  const catalogDropdownRef = useRef(null);
  const location = useLocation();
  const resourcesDropdownRef = useRef(null);
  const chartsDropdownRef = useRef(null);
  const mobileResourcesRef = useRef(null);
  const mobileChartsRef = useRef(null);

  const isActive = (path) => location.pathname === path;

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);

  const toggleResourcesDropdown = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setIsResourcesDropdownOpen((prevState) => {
      if (!prevState) {
        setIsChartsDropdownOpen(false);
        setIsCatalogDropdownOpen(false); // Add this line
      }
      return !prevState;
    });
  };

  const toggleCatalogDropdown = () => {
    setIsCatalogDropdownOpen(!isCatalogDropdownOpen);
    // Close other dropdowns if they're open
    if (isChartsDropdownOpen) setIsChartsDropdownOpen(false);
    if (isResourcesDropdownOpen) setIsResourcesDropdownOpen(false); // Add this line
  };

  const toggleChartsDropdown = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setIsChartsDropdownOpen((prevState) => {
      if (!prevState) {
        setIsResourcesDropdownOpen(false);
        setIsCatalogDropdownOpen(false); // Add this line
      }
      return !prevState;
    });
  };

  const toggleMobileCharts = () => setIsMobileChartsOpen((prev) => !prev);
  const toggleMobileResources = () => setIsMobileResourcesOpen((prev) => !prev);

  const handleExternalLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    setIsResourcesDropdownOpen(false);
    setIsChartsDropdownOpen(false);
    setIsCatalogDropdownOpen(false); // Add this line
    setIsMobileChartsOpen(false);
    setIsMobileResourcesOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (window.innerWidth < 640) return;
      if (resourcesDropdownRef.current && !resourcesDropdownRef.current.contains(event.target) && isResourcesDropdownOpen) {
        setIsResourcesDropdownOpen(false);
      }
      if (chartsDropdownRef.current && !chartsDropdownRef.current.contains(event.target) && isChartsDropdownOpen) {
        setIsChartsDropdownOpen(false);
      }
      if (catalogDropdownRef.current && !catalogDropdownRef.current.contains(event.target) && isCatalogDropdownOpen) {
        setIsCatalogDropdownOpen(false);
      }
    };
    document.body.addEventListener('click', handleClickOutside);
    return () => document.body.removeEventListener('click', handleClickOutside);
  }, [isResourcesDropdownOpen, isChartsDropdownOpen, isCatalogDropdownOpen]);

  return (
    <nav className="bg-white shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left section with logo and navigation */}
          <div className="flex items-center flex-1">
            <div className="flex-shrink-0">
              <Link
                to="/"
                className="font-bold text-xl bg-gradient-to-r from-blue-700 via-purple-700 to-indigo-600 text-transparent bg-clip-text tracking-tight"
                style={{ textShadow: "0 0 1px rgba(0,0,0,0.1)" }}
              >
                SynaptopathyDB
              </Link>
            </div>

            {/* Desktop navigation */}
            <div className="hidden sm:flex sm:items-center sm:ml-6 sm:space-x-4 flex-1">
              <Link
                to="/"
                className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-16 whitespace-nowrap ${isActive('/') ? 'border-blue-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }`}
              >
                Home
              </Link>
              <Link
                to="/gene-search"
                className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-16 whitespace-nowrap ${isActive('/gene-search') ? 'border-blue-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }`}
              >
                Gene Search
              </Link>
              <Link
                to="/disease-search"
                className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-16 whitespace-nowrap ${isActive('/disease-search') ? 'border-blue-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }`}
              >
                Disease Search
              </Link>
              <Link
                to="/paper-search"
                className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-16 whitespace-nowrap ${isActive('/paper-search') ? 'border-blue-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }`}
              >
                Publication Search
              </Link>

              {/* Catalog Dropdown */}
              <div className="relative" ref={catalogDropdownRef}>
                <button
                  onClick={toggleCatalogDropdown}
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-16 whitespace-nowrap ${isActive('/gene-table') || isActive('/disease-table') || isActive('/paper-table')
                    ? 'border-blue-500 text-gray-900'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    }`}
                >
                  Catalog
                  <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${isCatalogDropdownOpen ? 'transform rotate-180' : ''}`} />
                </button>
                {isCatalogDropdownOpen && (
                  <div className="absolute z-10 left-0 mt-1 w-screen max-w-md sm:px-0">
                    <div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden">
                      <div className="relative grid gap-6 bg-white px-5 py-6 sm:gap-8 sm:p-8">
                        <Link
                          to="/gene-table"
                          className="flex items-start p-3 -m-3 rounded-lg hover:bg-gray-50"
                          onClick={() => setIsCatalogDropdownOpen(false)}
                        >
                          <div className="ml-4">
                            <p className="text-base font-medium text-gray-900">Synapse Proteome Catalog</p>
                            <p className="mt-1 text-sm text-gray-500">Complete proteome catalog</p>
                          </div>
                        </Link>
                        <Link
                          to="/disease-table"
                          className="flex items-start p-3 -m-3 rounded-lg hover:bg-gray-50"
                          onClick={() => setIsCatalogDropdownOpen(false)}
                        >
                          <div className="ml-4">
                            <p className="text-base font-medium text-gray-900">Diseases Catalog</p>
                            <p className="mt-1 text-sm text-gray-500">All classified diseases</p>
                          </div>
                        </Link>
                        <Link
                          to="/paper-table"
                          className="flex items-start p-3 -m-3 rounded-lg hover:bg-gray-50"
                          onClick={() => setIsCatalogDropdownOpen(false)}
                        >
                          <div className="ml-4">
                            <p className="text-base font-medium text-gray-900">Publications Catalog</p>
                            <p className="mt-1 text-sm text-gray-500">Research publications</p>
                          </div>
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Charts Dropdown */}
              <div className="relative" ref={chartsDropdownRef}>
                <button
                  onClick={toggleChartsDropdown}
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-16 whitespace-nowrap ${isActive('/synaptopathy-history') ? 'border-blue-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    }`}
                >
                  Synaptopathy
                  <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${isChartsDropdownOpen ? 'transform rotate-180' : ''}`} />
                </button>
                {isChartsDropdownOpen && (
                  <div className="absolute z-10 left-0 mt-1 w-screen max-w-md sm:px-0">
                    <div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden">
                      <div className="relative grid gap-6 bg-white px-5 py-6 sm:gap-8 sm:p-8">
                        <Link
                          to="/synaptopathy-history"
                          className="flex items-start p-3 -m-3 rounded-lg hover:bg-gray-50"
                          onClick={() => setIsChartsDropdownOpen(false)}
                        >
                          <div className="ml-4">
                            <p className="text-base font-medium text-gray-900">Synaptopathy History</p>
                            <p className="mt-1 text-sm text-gray-500">View historical data on Synaptopathy</p>
                          </div>
                        </Link>
                        {/* <Link
                          to="/synapse-localization"
                          className="flex items-start p-3 -m-3 rounded-lg hover:bg-gray-50"
                          onClick={() => setIsChartsDropdownOpen(false)}
                        >
                          <div className="ml-4">
                            <p className="text-base font-medium text-gray-900">Synapse Compartments</p>
                            <p className="mt-1 text-sm text-gray-500">Detailed Synapse Protein Localization</p>
                          </div>
                        </Link> */}
                        <Link
                          to="/brain-region-protein-distribution"
                          className="flex items-start p-3 -m-3 rounded-lg hover:bg-gray-50"
                          onClick={() => setIsChartsDropdownOpen(false)}
                        >
                          <div className="ml-4">
                            <p className="text-base font-medium text-gray-900">Brain Region Protein Distribution</p>
                            <p className="mt-1 text-sm text-gray-500">Distribution of consensus proteins across brain regions and compartments</p>
                          </div>
                        </Link>
                        <Link
                          to="/disease-classifications"
                          className="flex items-start p-3 -m-3 rounded-lg hover:bg-gray-50"
                          onClick={() => setIsChartsDropdownOpen(false)}
                        >
                          <div className="ml-4">
                            <p className="text-base font-medium text-gray-900">International Classification of Diseases</p>
                            <p className="mt-1 text-sm text-gray-500">Synaptopathies Across Body Systems</p>
                          </div>
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Resources Dropdown */}
              <div className="relative" ref={resourcesDropdownRef}>
                <button
                  onClick={toggleResourcesDropdown}
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-16 whitespace-nowrap ${isActive('/resources') || isActive('/documentation') ? 'border-blue-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    }`}
                >
                  Resources
                  <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${isResourcesDropdownOpen ? 'transform rotate-180' : ''}`} />
                </button>
                {isResourcesDropdownOpen && (
                  <div className="absolute z-10 left-0 mt-1 w-screen max-w-md sm:px-0">
                    <div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden">
                      <div className="relative grid gap-6 bg-white px-5 py-6 sm:gap-8 sm:p-8">
                        <Link to="/faq" className="flex items-start p-3 -m-3 rounded-lg hover:bg-gray-50" onClick={() => setIsResourcesDropdownOpen(false)}>
                          <div className="ml-4">
                            <p className="text-base font-medium text-gray-900">FAQ</p>
                            <p className="mt-1 text-sm text-gray-500">Frequently Asked Questions</p>
                          </div>
                        </Link>
                        <Link to="/documentation" className="flex items-start p-3 -m-3 rounded-lg hover:bg-gray-50" onClick={() => setIsResourcesDropdownOpen(false)}>
                          <div className="ml-4">
                            <p className="text-base font-medium text-gray-900">API Documentation</p>
                            <p className="mt-1 text-sm text-gray-500">Try API endpoints and Export CSV data</p>
                          </div>
                        </Link>
                        <Link to="/SynapticERDiagram" className="flex items-start p-3 -m-3 rounded-lg hover:bg-gray-50" onClick={() => setIsResourcesDropdownOpen(false)}>
                          <div className="ml-4">
                            <p className="text-base font-medium text-gray-900">Entity Relationship Diagram</p>
                            <p className="mt-1 text-sm text-gray-500">Visualizing the application's database structure and relationships</p>
                          </div>
                        </Link>
                        <Link to="/publication" className="flex items-start p-3 -m-3 rounded-lg hover:bg-gray-50" onClick={() => setIsResourcesDropdownOpen(false)}>
                          <div className="ml-4">
                            <p className="text-base font-medium text-gray-900">Publication & Database Information</p>
                          </div>
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/about"
                className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-16 whitespace-nowrap ${isActive('/about') ? 'border-blue-500 text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }`}
              >
                About
              </Link>
            </div>
          </div>

          {/* Search bar moved to the right */}
          <div className="hidden sm:flex sm:items-center ml-4">
            <div className="w-64">
              <UnifiedSearch isCompact={true} />
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center sm:hidden z-50">
            <button
              onClick={toggleMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            >
              <span className="sr-only">Open main menu</span>
              {isMenuOpen ? <X className="block h-6 w-6" /> : <Menu className="block h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="sm:hidden">
          <div className="pt-2 pb-3 space-y-1">
            <div className="px-4 py-3">
              <UnifiedSearch isCompact={true} />
            </div>
            <Link to="/" className={`block pl-3 pr-4 py-2 text-base font-medium ${isActive('/') ? 'bg-blue-50 border-l-4 border-blue-500 text-blue-700' : 'border-l-4 border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800'}`}>
              Home
            </Link>
            <Link to="/gene-search" className={`block pl-3 pr-4 py-2 text-base font-medium ${isActive('/gene-search') ? 'bg-blue-50 border-l-4 border-blue-500 text-blue-700' : 'border-l-4 border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800'}`}>
              Gene Search
            </Link>
            <Link to="/disease-search" className={`block pl-3 pr-4 py-2 text-base font-medium ${isActive('/disease-search') ? 'bg-blue-50 border-l-4 border-blue-500 text-blue-700' : 'border-l-4 border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800'}`}>
              Disease Search
            </Link>
            <Link to="/paper-search" className={`block pl-3 pr-4 py-2 text-base font-medium ${isActive('/paper-search') ? 'bg-blue-50 border-l-4 border-blue-500 text-blue-700' : 'border-l-4 border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800'}`}>
            Publication Search
            </Link>
            
            {/* Mobile Catalog Dropdown */}
            <div className="border-l-4 border-transparent">
              <button
                onClick={toggleCatalogDropdown}
                className="w-full flex justify-between items-center pl-3 pr-4 py-2 text-base font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
              >
                Catalog
                <ChevronDown className={`h-5 w-5 transition-transform ${isCatalogDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {isCatalogDropdownOpen && (
                <div className="pl-6 pr-4 py-2 space-y-2 border-l-4 border-transparent bg-gray-50">
                  <a href="/gene-table" className="block py-2 text-sm text-gray-600 hover:text-gray-800">Synapse Proteome Catalog</a>
                  <a href="/disease-table" className="block py-2 text-sm text-gray-600 hover:text-gray-800">Diseases Catalog</a>
                  <a href="/paper-table" className="block py-2 text-sm text-gray-600 hover:text-gray-800">Publications Catalog</a>
                </div>
              )}
            </div>

            {/* Mobile Charts Dropdown */}
            <div className="border-l-4 border-transparent">
              <button
                onClick={toggleMobileCharts}
                className="w-full flex justify-between items-center pl-3 pr-4 py-2 text-base font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
              >
                Synaptopathy
                <ChevronDown className={`h-5 w-5 transition-transform ${isMobileChartsOpen ? 'rotate-180' : ''}`} />
              </button>
              {isMobileChartsOpen && (
                <div id="mobile-charts-content" className="pl-6 pr-4 py-2 space-y-2 border-l-4 border-transparent bg-gray-50" ref={mobileChartsRef}>
                  <a href="/synaptopathy-history" className="block py-2 text-sm text-gray-600 hover:text-gray-800">Synaptopathy History</a>
                  {/* <a href="/synapse-localization" className="block py-2 text-sm text-gray-600 hover:text-gray-800">Synapse Compartments</a> */}
                  <a href="/brain-region-protein-distribution" className="block py-2 text-sm text-gray-600 hover:text-gray-800">Brain Region Protein Distribution</a>
                  <a href="/disease-classifications" className="block py-2 text-sm text-gray-600 hover:text-gray-800">International Classification of Diseases</a>
                </div>
              )}
            </div>

            {/* Mobile Resources Dropdown */}
            <div className="border-l-4 border-transparent">
              <button
                onClick={toggleMobileResources}
                className="w-full flex justify-between items-center pl-3 pr-4 py-2 text-base font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
              >
                Resources
                <ChevronDown className={`h-5 w-5 transition-transform ${isMobileResourcesOpen ? 'rotate-180' : ''}`} />
              </button>
              {isMobileResourcesOpen && (
                <div id="mobile-resources-content" className="pl-6 pr-4 py-2 space-y-2 border-l-4 border-transparent bg-gray-50" ref={mobileResourcesRef}>
                  <a href="/faq" className="block py-2 text-sm text-gray-600 hover:text-gray-800">Frequently Asked Questions</a>
                  <a href="/documentation" className="block py-2 text-sm text-gray-600 hover:text-gray-800">API Documentation</a>
                  <a href="/SynapticERDiagram" className="block py-2 text-sm text-gray-600 hover:text-gray-800">Entity Relationship Diagram</a>
                  <a href="/publication" className="block py-2 text-sm text-gray-600 hover:text-gray-800">Publication & Database Information</a>
                </div>
              )}
            </div>

            <Link to="/about" className={`block pl-3 pr-4 py-2 text-base font-medium ${isActive('/about') ? 'bg-blue-50 border-l-4 border-blue-500 text-blue-700' : 'border-l-4 border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800'}`}>
              About
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default NavBar;